import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageSquare, Mail, ShieldCheck, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PATHS, countries, fmtDay, useLang, useT } from "../lib/i18n";
import { track } from "../lib/tracking";
import { usePoll } from "../lib/usePoll";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { NoIndex } from "../components/site/Seo";
import { TipGrid } from "../components/site/TipCard";
import { FreeVipHero, FreeVipSteps, FreeVipBenefits, FreeVipFaq, FreeVipNote } from "../components/site/FreeVipPitch";
import { Section } from "../components/site/Blocks";
import { BookieList } from "./VipPage";
import { SmsDialog } from "../components/site/SmsActivation";
import { Wrap } from "./PublicPage";
import { WelcomeModal, PerfBanner, Checklist, BookieHero, Packages, connectTelegram } from "../components/site/MemberFunnel";

function Details({ tip, onClose }) {
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { if (tip) { setD(null); api.get(`/me/tips/${tip.id}/details`).then((r) => setD(r.data)); } }, [tip]);
  return (
    <Dialog open={!!tip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tip-details-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{tip?.home} vs {tip?.away}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-muted-foreground">…</p> : (
          <div className="space-y-4 text-sm">
            {d.odds && <p className="font-num">{d.odds.bookmaker}: <b>{d.odds.home_od}</b> / <b>{d.odds.away_od}</b></p>}
            <p className="font-semibold">Head-to-Head</p>
            {d.h2h.length ? d.h2h.map((e) => <p key={e.id} className="flex justify-between border-b border-border pb-1"><span>{e.home} – {e.away}</span><span className="font-num">{e.ss}</span></p>)
              : <p className="text-muted-foreground">{t("Keine H2H-Daten.", "No H2H data.")}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubTab({ d, user }) {
  const t = useT();
  const lang = useLang();
  const [inv, setInv] = useState([]);
  useEffect(() => { api.get("/me/invoices").then((r) => setInv(r.data)).catch(() => {}); }, []);
  const portal = async () => { try { const { data } = await api.post("/me/portal"); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-8">
      <div className="grid gap-3 md:grid-cols-2" data-testid="entitlements-list">
        {d.entitlements.length ? d.entitlements.map((e) => (
          <div key={e.id} className="surface p-5">
            <p className="text-xs uppercase tracking-wider text-[var(--ball)]">{e.product}{e.sms ? " + SMS" : ""}</p>
            <p className="mt-1 font-display text-2xl font-bold">{e.scope === "all" ? t("Alle Tipps inkl. Grand Slams", "All tips incl. Grand Slams") : e.scope === "all_non_gs" ? t("Alle Tipps außer Grand Slams", "All tips except Grand Slams") : `Grand Slam: ${e.gs_name || ""}`}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t("Gültig bis", "Valid until")} {fmtDay(e.valid_until, lang)}{e.cancel_at_period_end ? ` · ${t("gekündigt", "canceled")}` : ""}</p>
          </div>
        )) : <div className="surface p-6 text-sm text-muted-foreground">{t("Kein aktives VIP.", "No active VIP.")} <Link to={PATHS[lang].vip} className="text-[var(--ball)]">{t("Jetzt VIP werden", "Become VIP")} →</Link></div>}
      </div>
      {user.stripe_customer_id && <Button variant="secondary" className="rounded-full" onClick={portal} data-testid="stripe-portal-btn">{t("Abo & Zahlungen verwalten", "Manage subscription & billing")}</Button>}
      <Section title={t("Rechnungen", "Invoices")}>
        <div className="surface divide-y divide-border text-sm" data-testid="invoices-list">
          {inv.length ? inv.map((i) => <a key={i.id} href={i.pdf || i.url} target="_blank" rel="noreferrer" className="flex justify-between px-4 py-3 hover:bg-secondary/50"><span>{i.number || i.id}</span><span className="font-num">{i.amount} {i.currency?.toUpperCase()} · {i.status}</span></a>)
            : d.payments.length ? d.payments.map((p) => <div key={p.id} className="flex justify-between px-4 py-3"><span>{p.product} · {p.type}</span><span className="font-num">{p.amount} €</span></div>)
              : <p className="px-4 py-3 text-muted-foreground">–</p>}
        </div>
      </Section>
    </div>
  );
}

function FreeVipTab({ d, user, reload }) {
  const t = useT();
  const { config } = useAuth();
  const [country, setCountry] = useState(user.country || "");
  const [bookie, setBookie] = useState(user.last_bookie_id || "");
  const [bookies, setBookies] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (country) api.get("/bookies", { params: { country } }).then((r) => setBookies(r.data)); }, [country]);
  const submit = async () => {
    if (!file || !bookie) { toast.error(t("Bitte Buchmacher und Screenshot wählen.", "Please choose bookmaker and screenshot.")); return; }
    const fd = new FormData();
    fd.append("bookie_id", bookie); fd.append("country", country); fd.append("file", file);
    setBusy(true);
    try { await api.post("/me/bookie-requests", fd); toast.success(t("Eingereicht – wir prüfen deinen Screenshot.", "Submitted – we will review your screenshot.")); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-8" data-testid="free-vip-tab">
      <FreeVipHero><FreeVipSteps /></FreeVipHero>
      <div className="grid gap-8 lg:grid-cols-2">
      <div><h3 className="mb-4 font-display text-2xl font-bold">1. {t("Buchmacher wählen", "Choose bookmaker")}</h3><BookieList country={country} setCountry={setCountry} /></div>
      <div className="surface p-6" data-testid="bookie-upload-form">
        <h3 className="font-display text-2xl font-bold">3. {t("Screenshot hochladen", "Upload screenshot")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t(`Transaktionsübersicht mit Einzahlung (mind. ${config?.bookie_min_deposit || 100} €). Alternativ als Foto an unseren Telegram-Bot senden.`, `Transaction overview showing your deposit (min. €${config?.bookie_min_deposit || 100}). Alternatively send it as a photo to our Telegram bot.`)}</p>
        <select value={bookie} onChange={(e) => setBookie(e.target.value)} className="mt-4 h-10 w-full rounded-lg border border-input bg-secondary px-3 text-sm" data-testid="bookie-upload-select">
          <option value="">{t("Buchmacher", "Bookmaker")}</option>{bookies.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border p-4 text-sm"><Upload size={16} />{file ? file.name : t("Bild auswählen", "Choose image")}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="bookie-upload-file" /></label>
        <Button className="mt-4 w-full rounded-full" disabled={busy} onClick={submit} data-testid="bookie-upload-submit">{t("Einreichen", "Submit")}</Button>
        <div className="mt-6 space-y-2 text-sm" data-testid="bookie-requests-list">
          {d.bookie_requests.map((r) => <p key={r.id} className="flex justify-between"><span>{r.bookie_name} · {fmtDay(r.created_at)}</span><span className={r.status === "approved" ? "text-[var(--win)]" : r.status === "rejected" ? "text-[var(--loss)]" : ""}>{r.status}{r.message ? ` – ${r.message}` : ""}</span></p>)}
        </div>
      </div>
      </div>
      <div className="grid gap-8 lg:grid-cols-2"><FreeVipBenefits /><FreeVipFaq /></div>
      <FreeVipNote />
    </div>
  );
}

function SettingsTab({ user, refresh }) {
  const t = useT();
  const lang = useLang();
  const [f, setF] = useState({ name: user.name || "", country: user.country || "", lang: user.lang || "de", channels: user.channels || {}, marketing_email: !!user.marketing_email_consent });
  const [smsOpen, setSmsOpen] = useState(false);
  const hasSms = (user.entitlements || []).some((e) => e.sms);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const save = async (extra = {}) => { try { await api.put("/me", { ...f, ...extra }); toast.success(t("Gespeichert", "Saved")); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const connectTg = async () => {
    try { await connectTelegram(refresh); } catch (e) { toast.error(errMsg(e)); }
  };
  const ch = (k, Icon, label, ok) => (
    <label className="surface flex items-center gap-3 p-4" key={k}><Icon size={18} className="text-[var(--ball)]" /><span className="flex-1 text-sm">{label}{!ok && <span className="block text-xs text-muted-foreground">{t("nicht verbunden/bestätigt", "not connected/verified")}</span>}</span>
      <Switch checked={!!f.channels[k]} onCheckedChange={(v) => setF({ ...f, channels: { ...f.channels, [k]: v } })} data-testid={`channel-toggle-${k}`} /></label>
  );
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <h3 className="font-display text-2xl font-bold">{t("Profil", "Profile")}</h3>
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="h-10 rounded-md border border-input bg-secondary px-2 text-sm" data-testid="settings-country">{countries(lang).map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select>
          <select value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} className="h-10 rounded-md border border-input bg-secondary px-2 text-sm" data-testid="settings-lang"><option value="de">Deutsch</option><option value="en">English</option></select>
        </div>
        <h3 className="pt-4 font-display text-2xl font-bold">{t("Zustellung", "Delivery channels")}</h3>
        {ch("email", Mail, "E-Mail", user.email_verified)}
        {ch("telegram", Send, "Telegram", !!user.telegram_chat_id)}
        {hasSms && ch("sms", MessageSquare, "SMS", user.phone_verified)}
        <label className="flex items-start gap-3 text-sm text-muted-foreground"><Checkbox checked={f.marketing_email} onCheckedChange={(v) => setF({ ...f, marketing_email: !!v })} data-testid="settings-marketing-email" />{t("Newsletter/Angebote per E-Mail", "Newsletter/offers by email")}</label>
        <Button className="rounded-full" onClick={() => save()} data-testid="settings-save">{t("Speichern", "Save")}</Button>
      </div>
      <div className="space-y-6">
        <div className="surface p-6" data-testid="telegram-connect">
          <h3 className="flex items-center gap-2 font-display text-2xl font-bold"><Send size={20} />Telegram</h3>
          {user.telegram_chat_id ? <p className="mt-2 text-sm text-[var(--win)]">✓ {t("Verbunden", "Connected")} {user.telegram_username ? `@${user.telegram_username}` : ""}</p>
            : <Button className="mt-3 rounded-full" onClick={connectTg} data-testid="telegram-connect-btn">{t("Telegram verbinden", "Connect Telegram")}</Button>}
        </div>
        {hasSms && (
          <div className="surface p-6" data-testid="phone-verify">
            <h3 className="flex items-center gap-2 font-display text-2xl font-bold"><ShieldCheck size={20} />{t("SMS-Nummer", "SMS number")}</h3>
            {user.phone_verified ? <p className="mt-1 text-sm text-[var(--win)]" data-testid="phone-verified">✓ {user.phone}</p> : <p className="mt-1 text-sm text-muted-foreground">{t("Noch nicht aktiviert", "Not activated yet")}</p>}
            <Button variant="secondary" className="mt-3 rounded-full" onClick={() => setSmsOpen(true)} data-testid="phone-change-btn">{user.phone_verified ? t("Nummer ändern", "Change number") : t("SMS aktivieren", "Activate SMS")}</Button>
            <SmsDialog open={smsOpen} onOpenChange={setSmsOpen} user={user} onDone={refresh} />
          </div>
        )}
        <div className="surface space-y-3 p-6">
          <h3 className="font-display text-2xl font-bold">{t("Passwort ändern", "Change password")}</h3>
          <Input type="password" placeholder={t("Aktuelles Passwort", "Current password")} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-password" />
          <Input type="password" placeholder={t("Neues Passwort", "New password")} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-password" />
          <Button variant="secondary" className="rounded-full" onClick={() => save(pw)} data-testid="settings-password-save">{t("Passwort speichern", "Save password")}</Button>
        </div>
      </div>
    </div>
  );
}

export default function AccountPage() {
  const t = useT();
  const lang = useLang();
  const { user, refresh, logout } = useAuth();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [det, setDet] = useState(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [tab, setTab] = useState("tips");  const load = useCallback(() => api.get("/me/dashboard").then((r) => setD(r.data)).catch(() => {}), []);
  const isVip = !!d?.entitlements?.length;
  useEffect(() => { if (user) load(); }, [user, load]);
  usePoll(load, 45000, !!user);
  useEffect(() => {
    if (sp.get("sms_activate") && !sp.get("session_id") && user?.needs_sms_activation) setSmsOpen(true);
  }, [sp, user]);
  useEffect(() => {
    const sid = sp.get("session_id");
    if (sp.get("checkout") !== "success" || !sid) return undefined;
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          const u = await refresh();
          load();
          if (sp.get("sms_activate") && u && !u.phone_verified) setSmsOpen(true);
        }
      } catch { /* keep polling */ }
      if (n > 15) clearInterval(iv);
    }, 2000);
    return () => clearInterval(iv);
  }, [sp, refresh, load]);
  useEffect(() => {
    if (sp.get("checkout") === "success" && !sessionStorage.getItem(`p_${sp.get("product")}_${sp.get("value")}`)) {
      sessionStorage.setItem(`p_${sp.get("product")}_${sp.get("value")}`, "1");
      track("purchase", { value: Number(sp.get("value")), currency: "EUR", product: sp.get("product"), items: [{ item_id: sp.get("product") }] });
      toast.success(t("Danke für deinen Kauf! Dein Zugang wird in wenigen Sekunden aktiviert.", "Thanks for your purchase! Access activates within seconds."));
      setTimeout(load, 4000);
    }
  }, [sp, t, load]);
  if (user === null) return null;
  if (!user) return <Navigate to={PATHS[lang].login} replace />;
  const resend = async () => { try { await api.post("/auth/resend-verification"); toast.success(t("Falls deine E-Mail-Adresse erreichbar ist, erhältst du in Kürze eine E-Mail.", "If your email address is reachable, you will receive an email shortly.")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Wrap>
      <NoIndex title={t("Konto", "Account")} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-sm text-muted-foreground">{user.email}</p><h1 className="font-display text-5xl font-extrabold" data-testid="account-title">{t("Mein Konto", "My account")}</h1></div>
        <div className="flex gap-2">{user.role === "admin" && <Link to="/admin"><Button variant="secondary" className="rounded-full" data-testid="admin-link">Admin</Button></Link>}<Button variant="ghost" className="rounded-full" onClick={logout} data-testid="logout-btn">Logout</Button></div>
      </div>
      {!user.email_verified && <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--ball)]/40 bg-[var(--ball)]/5 p-4 text-sm" data-testid="verify-email-banner">{t("Bitte bestätige deine E-Mail-Adresse, um Tipps per E-Mail zu erhalten.", "Please confirm your email to receive tips by email.")}<Button size="sm" variant="secondary" onClick={resend} data-testid="resend-verification-btn">{t("Erneut senden", "Resend")}</Button></div>}
      {user.needs_sms_activation && <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--ball)] bg-[var(--ball)]/10 p-4 text-sm" data-testid="sms-activation-banner"><span className="flex items-center gap-2 font-semibold"><MessageSquare size={16} />{t("SMS-Benachrichtigung aktivieren – bestätige deine Handynummer, um SMS zu neuen Tipps zu erhalten.", "Activate SMS notifications – verify your mobile number to receive SMS about new tips.")}</span><Button size="sm" onClick={() => setSmsOpen(true)} data-testid="sms-activation-banner-btn">{t("Jetzt aktivieren", "Activate now")}</Button></div>}
      <SmsDialog open={smsOpen} onOpenChange={setSmsOpen} user={user} onDone={refresh} />
      <WelcomeModal user={user} refresh={refresh} />
      {d && !isVip && <PerfBanner perf={d.vip_perf} />}
      {d && !isVip && <BookieHero d={d} user={user} onUpload={() => setTab("free")} />}
      {d && <Checklist c={d.checklist} onTab={setTab} />}
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="h-auto flex-wrap bg-secondary">
          {[["tips", t("Tipps", "Tips")], ["sub", "Abo / VIP"], ["free", t("Gratis VIP", "Free VIP")], ["settings", t("Einstellungen", "Settings")]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`account-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="tips">{d && <><Section title={t("Aktuelle Tipps", "Current tips")}><TipGrid tips={d.current} onDetails={setDet} /></Section><Section title={t("Historie", "History")}><TipGrid tips={d.history} /></Section>{!isVip && <Packages d={d} />}</>}</TabsContent>
        <TabsContent value="sub" className="pt-6">{d && <SubTab d={d} user={user} />}</TabsContent>
        <TabsContent value="free" className="pt-6">{d && <FreeVipTab d={d} user={user} reload={load} />}</TabsContent>
        <TabsContent value="settings" className="pt-6"><SettingsTab user={user} refresh={refresh} /></TabsContent>
      </Tabs>
      <Details tip={det} onClose={() => setDet(null)} />
    </Wrap>
  );
}
